"use client";

import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { FormField, FormItem, FormControl, FormMessage, FormLabel } from "@/components/ui/form";
import { useFormContext } from "react-hook-form";
import { StepProps, BaseFormData } from "../components/types";
import { ValidationRules } from "../components/FormValidation";
import { cn } from "@/lib/utils";

export interface CheckboxOption {
  value: string;
  label: string;
  description?: string; // Optional second line with normal weight
  clearOtherOptions?: boolean; // If true, checking this option clears all others
}

export interface CheckboxListStepConfig {
  title?: string;
  description?: string;
  fieldName: string;
  options: CheckboxOption[];
  isRequired?: boolean;
}

type CheckboxListStepProps<T = BaseFormData> = StepProps<T>;

export const CheckboxListStep = <T extends BaseFormData = BaseFormData>(props: CheckboxListStepProps<T>) => {
  const { title, description } = props;
  const { control } = useFormContext();
  
  // Extract stepConfig with type safety - use any to bypass TypeScript restrictions
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const stepConfig = props.stepConfig as any;
  
  const config = stepConfig || {
    title: "Select your options",
    description: undefined,
    fieldName: "selections",
    options: [],
    isRequired: true
  };

  return (
    <div className="flex flex-col gap-12">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <h3 className="paragraph-xl text-foreground font-bold">
          {title || config.title}
        </h3>
        {description && (
          <p className="paragraph-lg text-muted-foreground">
            {description}
          </p>
        )}
      </div>

      {/* Checkbox Selection Section */}
      <div className="flex flex-col gap-6">
        
        
        
        <FormField
          control={control}
          name={config.fieldName as never}
          rules={config.isRequired ? ValidationRules.arrayMinLength("option", 1) : { validate: () => true }}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="paragraph-base text-foreground font-bold">
                {config.title}
                {config.description && (
                  <p className="paragraph-base text-muted-foreground">
                    {config.description}
                  </p>
                )}
              </FormLabel>
              <FormControl>
                <div className="border border-border rounded-3xl gap-0 bg-white">
                  {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                  {(config.options as any[]).map((option: any, index: number) => {
                    const current: string[] = Array.isArray(field.value) ? (field.value as string[]) : [];
                    const checked = current.includes(option.value);
                    
                    // Check if any option with clearOtherOptions is checked
                    const hasClearOptionChecked = current.some(checkedValue => {
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      const checkedOption = (config.options as any[]).find(opt => opt.value === checkedValue);
                      return checkedOption?.clearOtherOptions;
                    });
                    
                    // Disable this option if another clearOtherOptions option is checked
                    const isDisabled = hasClearOptionChecked && !checked;
                    
                    return (
                      <Label 
                        htmlFor={option.value}  
                        key={option.value} 
                        className={cn(
                          "w-full px-6 py-4 flex items-center gap-6 transition-colors",
                          isDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-muted",
                          index === 0 && "rounded-t-3xl",
                          index === config.options.length - 1 && "rounded-b-3xl",
                          index < config.options.length - 1 && "border-b border-border"
                        )}
                        onClick={isDisabled ? (e) => e.preventDefault() : undefined}
                      >
                        <Checkbox
                          id={option.value}
                          checked={checked}
                          disabled={isDisabled}
                          onCheckedChange={(isChecked) => {
                            let next: string[];
                            
                            if (isChecked) {
                              if (option.clearOtherOptions) {
                                // If this option clears others, only select this one
                                next = [option.value];
                              } else {
                                // Normal behavior - add to existing selections
                                const nextSet = new Set<string>(current);
                                nextSet.add(option.value);
                                next = Array.from(nextSet);
                              }
                            } else {
                              // Remove this option
                              const nextSet = new Set<string>(current);
                              nextSet.delete(option.value);
                              next = Array.from(nextSet);
                            }
                            
                            field.onChange(next);
                          }}
                          size="large"
                          className="cursor-pointer"
                        />
                        <div className="flex-1 cursor-pointer flex flex-col gap-1">
                          <div className="paragraph-base font-bold text-foreground">
                            {option.label}
                          </div>
                          {option.description && (
                            <div className="paragraph-base text-muted-foreground font-normal">
                              {option.description}
                            </div>
                          )}
                        </div>
                      </Label>
                    );
                  })}
                </div>
              </FormControl>
              {config.isRequired && <FormMessage />}
            </FormItem>
          )}
        />
      </div>

    </div>
  );
};
